import { createFileRoute, Link } from "@tanstack/react-router";
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { Inbox, Loader2, Mail, Phone, RotateCcw, User } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { useConfirm } from "@/components/DialogProvider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { db } from "@/firebase";
import type { WorkerModel } from "@/firestore_types";

export const Route = createFileRoute("/dashboard/admin/trash/workers")({
  component: RouteComponent,
});

function RouteComponent() {
  const [deletedWorkers, setDeletedWorkers] = useState<WorkerModel[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const workersQuery = query(
      collection(db, "Workers"),
      where("isDeleted", "==", true)
    );

    const unsubscribe = onSnapshot(workersQuery, (snapshot) => {
      const workersData = snapshot.docs.map((doc) => ({
        ...(doc.data() as WorkerModel),
        id: doc.id,
      }));
      setDeletedWorkers(workersData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {deletedWorkers.length === 0 ? (
        <EmptyState />
      ) : (
        deletedWorkers.map((worker) => <WorkerCard key={worker.id} worker={worker} />)
      )}
    </div>
  );
}

interface WorkerCardProps {
  worker: WorkerModel;
}

function WorkerCard({ worker }: WorkerCardProps) {
  const { id, name, email, phoneNumber } = worker;

  return (
    <Card className="border-muted/60">
      <CardHeader className="gap-4 md:flex md:items-center md:justify-between">
        <div className="space-y-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
            <Link
              to="/dashboard/admin/workers/$workerId"
              params={{ workerId: id }}
              className="cursor-pointer text-blue-600 transition-colors hover:text-blue-800 hover:underline"
            >
              {name}
            </Link>
          </CardTitle>
        </div>
        <CardAction>
          <RestoreWorkerButton workerId={id} workerName={name} />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        <InfoBlock
          icon={<User className="text-muted-foreground size-4" />}
          label="Name"
          value={name}
        />
        {email ? (
          <InfoBlock
            icon={<Mail className="text-muted-foreground size-4" />}
            label="Email"
            value={email}
          />
        ) : null}
        <InfoBlock
          icon={<Phone className="text-muted-foreground size-4" />}
          label="Phone number"
          value={phoneNumber || "Not provided"}
        />
      </CardContent>
      <CardFooter className="text-muted-foreground flex-col items-start gap-2 text-xs md:flex-row md:items-center md:justify-between">
        <p>Restoring a worker will make them visible to admins and managers again.</p>
      </CardFooter>
    </Card>
  );
}

interface InfoBlockProps {
  icon: ReactNode;
  label: string;
  value: string;
}

function InfoBlock({ icon, label, value }: InfoBlockProps) {
  return (
    <div className="border-muted flex flex-col gap-1 rounded-lg border border-dashed p-4">
      <span className="text-muted-foreground flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
        {icon}
        {label}
      </span>
      <span className="text-foreground text-sm font-medium">{value}</span>
    </div>
  );
}

interface RestoreWorkerButtonProps {
  workerId: string;
  workerName: string;
}

function RestoreWorkerButton({ workerId, workerName }: RestoreWorkerButtonProps) {
  const [isRestoring, setIsRestoring] = useState(false);

  const confirm = useConfirm();

  const handleRestore = async () => {
    const confirmed = await confirm({
      title: "Restore worker",
      description: `"${workerName}" will reappear everywhere active workers are shown.`,
    });

    if (!confirmed) return;

    setIsRestoring(true);
    try {
      const workerRef = doc(db, "Workers", workerId);
      await updateDoc(workerRef, {
        isDeleted: false,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Restored "${workerName}"`);
    } catch (error) {
      console.error("Failed to restore worker", error);
      toast.error("Something went wrong while restoring the worker.");
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <Button onClick={handleRestore} size="sm" variant="outline" disabled={isRestoring}>
      {isRestoring ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Restoring...
        </>
      ) : (
        <>
          <RotateCcw className="size-4" />
          Restore
        </>
      )}
    </Button>
  );
}

function EmptyState() {
  return (
    <div className="border-muted bg-muted/30 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-8 py-16 text-center">
      <Inbox className="text-muted-foreground size-10" />
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">No deleted workers</p>
        <p className="text-muted-foreground text-sm">
          Recently deleted workers will appear here for quick recovery.
        </p>
      </div>
    </div>
  );
}
